import { humanizeInternalTerms } from "@/shared/lib/internalCodes";
import { maskVendor } from "@/shared/lib/vendorMask";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { ratioToCss } from "@/shared/lib/video/aspect";

import type { VideoAssetDto } from "../../api/videoService";
import { channelName, channelTag } from "../../model/videoChannelTag";
import { chunkEditable, chunkFailureHint } from "../../model/chunkEdit";
import { useFrozenAssetUrl } from "../../model/useFrozenAssetUrl";

/**
 * 段(CHUNK)展示网格(P2d-2 展示 / P2d-3 接线重渲)。每段一个 <video> 预览 + 含的镜号 +
 * 「重渲此段」(传 onRegen 则可点;渲染中/某段重渲中禁用)。旧每镜(SHOT)任务走详情页 per-shot 网格。
 *
 * ★V4(2026-07-16):按**段态**渲染,不再「有 videoUrl 才画一格」。
 *
 * 用户报的现象:「ai 编排之后生成了三段,点击生成之后详情页只有一段,没有把另外两段列出来」。
 * 根因就在这里 —— 本组件循环的是**已出产物**:没出的段在 DOM 里根本不存在,而「段 N/M」的
 * M 取 `sorted.length` = **已出数**,不是计划数 → 一个三段的任务在出第一段时,长得和一个
 * 一段的任务一模一样。用户以为编排丢了两段,其实是页面没画。
 *
 * 现在 ai 会在渲染**开始前**下发 N 条 PENDING 段,再逐段推进 PENDING → RUNNING → SUCCESS/FAILED,
 * 本组件照着行渲染即可 —— 分母天然是计划数。旧任务(没有计划行)行为不变:有几行画几行。
 */
const CHUNK_STATE: Record<
  string,
  { label: string; variant: "outline" | "secondary" | "destructive" }
> = {
  PENDING: { label: "排队中", variant: "outline" },
  RUNNING: { label: "生成中", variant: "secondary" },
  FAILED: { label: "失败", variant: "destructive" },
  BLOCKED: { label: "已拦截", variant: "destructive" },
};

/**
 * 波3-⑥ 降级留痕:归因 → 用户话术。只认已实证的归因(ai 侧 frame_degrade 同一口径);
 * 没见过的归因给通用措辞,**不猜**具体原因 —— 概览不许比实际乐观,也不许比实际具体。
 */
function degradeLabel(reason?: string): string {
  if (reason === "input_image_rejected") {
    return "本段人像首帧被平台内容安全拒收 · 已降级为文字生成(丢帧保出片)";
  }
  return "本段已从图生视频降级为文字生成";
}
export function VideoChunkGrid({
  chunks,
  ratio,
  onRegen,
  onEditText,
  regenSeq,
  busy,
  shotScenes,
  shotVoiceovers,
  shotMotions,
  taskSettled,
  canResume,
}: {
  chunks: VideoAssetDto[];
  ratio: string;
  onRegen?: (seq: number) => void;
  /** VID-D:打开该段的「改文案」面板(传了才出这个入口)。 */
  onEditText?: (seq: number) => void;
  regenSeq?: number | null;
  busy?: boolean;
  /**
   * 任务是否已经**终态**(PARTIAL/FAILED/CANCELLED —— 不会再自己往下跑了)。
   *
   * ★为什么非要这一格:此前本组件**根本不知道任务状态**,于是「在途 PENDING」和
   * 「终态 PENDING」在它眼里**长得一模一样** —— 都渲染成「排队中,等前面的段渲完」。
   * 而终态下前面那段往往**已经 FAILED**,这句话就把"永远等不到"说成了"再等等"
   * (0819 真浏览器实测:PARTIAL 任务里两个 PENDING 段仍这么写,且它们**一个操作入口都没有**)。
   */
  taskSettled?: boolean;
  /**
   * 「继续生成」那颗按钮**这一刻在不在**(= 控制条的 `chunkTally.partial`)。
   *
   * ★0829 走查实据:整单 FAILED、段 1 失败段 2–4 PENDING 时,`delivered=0` ⇒ `partial=false`
   *   ⇒ 按钮不渲染,而本组件的终态文案照旧写「点『继续生成』补齐后段」——**指向一颗不存在的
   *   按钮**。本文件上游注释早就记过这个坑(提示与按钮各挂各的判据),当时只修了控制条自己
   *   那一对,段卡这句没跟上。这一格就是把两边接到**同一个值**上。
   * ★默认(不传)按「按钮不在」处理:老调用点不会因为漏传而承诺一个不存在的东西 ——
   *   宁可少说一句,不可指错路。
   */
  canResume?: boolean;
  /** 每镜文案(1-based 镜号对 requestPayload 数组下标 -1;2026-07-13 生产反馈:chunk 任务详情页看不到分镜文案)。 */
  shotScenes?: string[];
  shotVoiceovers?: string[];
  // ★契约 v2:每镜不再有 per-shot prompt(最终 prompt 按段编译)。展示每镜「动作」(panelMotions)
  // —— 环境在上、动作在下,合起来就是这一镜「在哪儿、做什么」,比展示编译产物对用户更直观。
  shotMotions?: string[];
}) {
  const frozenUrl = useFrozenAssetUrl();
  const sorted = [...chunks].sort((a, b) => a.seq - b.seq);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {sorted.map((c) => {
        const seqs = c.shotSeqs ?? [];
        const shotLabel =
          seqs.length > 0
            ? `含镜 ${seqs[0]}${seqs.length > 1 ? `–${seqs[seqs.length - 1]}` : ""}`
            : "";
        const regenning = regenSeq === c.seq;
        const state = CHUNK_STATE[c.status];
        const pending = c.status === "PENDING" || c.status === "RUNNING";
        const failed = c.status === "FAILED" || c.status === "BLOCKED";
        const hint = chunkFailureHint(c);
        const canEditText = Boolean(onEditText) && chunkEditable(c);
        return (
          <div key={c.id} className="rounded-lg border bg-card p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono">
                段 {c.seq}/{sorted.length}
              </Badge>
              {state && (
                <Badge variant={state.variant} className="text-2xs">
                  {state.label}
                </Badge>
              )}
              {shotLabel && <span className="text-xs text-muted-foreground">{shotLabel}</span>}
              <span className="text-xs text-muted-foreground">
                {c.durationSec ? `${c.durationSec}s` : ""}
              </span>
            </div>
            <div
              className="relative overflow-hidden rounded bg-black max-h-72 flex items-center justify-center"
              style={{ aspectRatio: ratioToCss(ratio, "9 / 16") }}
            >
              {c.videoUrl ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video
                  /* ★不许直接用 c.videoUrl:在途任务每轮轮询都重签 URL,src 一变播放就重来
                     (0923 真机症状「点某一段播一秒就不动」)。见 useFrozenAssetUrl 头注。 */
                  src={frozenUrl(c.id, c.generatedAt, c.videoUrl)}
                  poster={frozenUrl(c.id, c.generatedAt, c.thumbUrl)}
                  controls
                  className="h-full w-full object-contain"
                />
              ) : (
                // 还没出片的段:说清楚它在等什么,而不是「本段暂无视频」——
                // 用户看到的是一条正在跑的任务,不是一个空洞。
                <span className="px-2 text-center text-xs text-muted-foreground">
                  {pending ? (
                    <span className="inline-flex items-center gap-1.5">
                      {c.status === "RUNNING" && (
                        <span
                          className="inline-block size-1.5 animate-pulse rounded-full bg-primary"
                          aria-hidden
                        />
                      )}
                      {c.status === "RUNNING"
                        ? "正在生成这一段…"
                        : taskSettled
                          ? // 终态:这一段不会再自己动了,指向真出口。
                            // ★不写方位(「上方/下方」会随布局变 —— 视频线刚整页重排过一次),
                            //   只写按钮名字:名字不会变。
                            // ★但只在那颗按钮**真的在**的时候才提它(canResume)——
                            //   否则退回只陈述事实,把「下一步」让给整单级的「整体重试」。
                            canResume
                            ? "这一段没渲成 · 点「继续生成」补齐后段"
                            : "这一段没渲成"
                          : // 在途:原话保持不动 —— 那时它是对的。
                            "排队中,等前面的段渲完"}
                    </span>
                  ) : (
                    // VID-D 的人话原因优先;没有再退 errorCode(0824:重渲判死后用户对着
                    // 一个裸 INTERNAL_ERROR 反复点重渲,原因其实在 failureReason 里)。
                    maskVendor(c.failureReason) || c.errorCode || "本段暂无视频"
                  )}
                </span>
              )}
            </div>
            {/* 波3-⑥ 降级留痕:判据是 degradedFrom 非空(degradeReason 只管措辞)。
                不显示 = 用户看到的是一段「正常的 T2V」,而他要的人物首帧其实被丢了。 */}
            {c.degradedFrom && (
              <p className="rounded bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-400">
                {degradeLabel(c.degradeReason)}
              </p>
            )}
            {/* 0925 通道亮账:紧挨降级信息的一枚小标,failover 过的写「原主通道→实际通道」。 */}
            {channelTag(c) && (
              <p className="text-xs">
                <span
                  data-testid="chunk-channel"
                  title={
                    c.failoverFrom
                      ? `主通道 ${channelName(c.failoverFrom)} 不可用,本段改走 ${channelName(c.channelUsed ?? "")} 出片`
                      : `本段经 ${channelName(c.channelUsed ?? "")} 通道出片`
                  }
                  className={
                    c.failoverFrom
                      ? "rounded bg-amber-500/10 px-1.5 py-0.5 font-mono text-amber-700 dark:text-amber-400"
                      : "rounded bg-muted px-1.5 py-0.5 font-mono text-muted-foreground"
                  }
                >
                  {channelTag(c)}
                </span>
              </p>
            )}
            {seqs.map((n) => {
              const scene = shotScenes?.[n - 1];
              const motion = shotMotions?.[n - 1];
              const vo = shotVoiceovers?.[n - 1];
              if (!scene && !motion && !vo) return null;
              return (
                <div key={n} className="space-y-0.5 text-xs">
                  {scene && (
                    <p className="text-muted-foreground line-clamp-2" title={scene}>
                      <span className="font-mono">镜{n}</span> {humanizeInternalTerms(scene)}
                    </p>
                  )}
                  {motion && (
                    <p className="text-muted-foreground line-clamp-2" title={motion}>
                      <span className="text-foreground">动作:</span>
                      {humanizeInternalTerms(motion)}
                    </p>
                  )}
                  {vo && (
                    <p className="text-foreground">
                      <span className="text-muted-foreground">口播:</span>
                      {vo}
                    </p>
                  )}
                </div>
              );
            })}
            {/* VID-D:失败段说清**为什么**,用户才知道该改哪一栏。原因由后端翻成人话
                (认不出时它自己会说「原因还没查清」)—— 前端不在这里再猜一次。 */}
            {failed && (
              <div
                data-testid={`chunk-failure-${c.seq}`}
                className="rounded bg-destructive/10 px-2 py-1.5 text-xs text-destructive"
              >
                <p className="leading-snug">{humanizeInternalTerms(hint.reason)}</p>
                {hint.code && <p className="mt-0.5 font-mono text-2xs opacity-60">{hint.code}</p>}
              </div>
            )}
            {/* audit-0713 C5:能力未接入时不渲染死按钮(硬编码「即将上线」不走 catalog,禁用态 tooltip 也弹不出) */}
            {/* V4:还没出片的段不给「重渲」——「重渲」一个从没渲过的东西对用户是句病句,
                而且它真会开一张计费 ticket 去重复渲染正在渲的那一段。 */}
            {!pending && (onRegen || canEditText) && (
              <div className="flex gap-2">
                {canEditText && (
                  <Button
                    size="sm"
                    variant={failed ? "default" : "outline"}
                    className="flex-1"
                    /* ★整片还在渲的时候**照样能改文案** —— 老板的原话就是「在各分段生成中时,
                       经常会有生成失败的段…没有地方去改文案」,那正是他最想改的时刻:
                       前面的段刚因为审核被拒,后面的段还在排队,他要趁现在把词换掉。
                       只保存不花钱、也不影响在跑的那一轮(改的是下一次重渲要读的文案);
                       真正不能在这时点的是「保存并重渲」,那一颗在面板里单独禁用。 */
                    disabled={regenSeq != null}
                    onClick={() => onEditText!(c.seq)}
                    aria-label={`改第 ${c.seq} 段的文案`}
                    title="改这一段的画面/动作/口播文案,再决定要不要重渲"
                  >
                    改文案
                  </Button>
                )}
                {onRegen && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    disabled={busy || regenSeq != null}
                    onClick={() => onRegen(c.seq)}
                    // 无障碍名要跟着状态走:静态名会让读屏用户听不出"这一段正在重渲"
                    aria-label={regenning ? `第 ${c.seq} 段重渲中` : `直接重渲第 ${c.seq} 段`}
                    title="不改文案,直接原样重渲此段(与后段硬切)"
                  >
                    {regenning ? "重渲中…" : "直接重渲"}
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
